import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { useSiteContent, trackEvent } from '../context/SiteContext';
import { trackLeadFormConversion } from '../utils/consent';
import './ContactForm.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactForm = () => {
  const { contact } = useSiteContent();
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', eventType: '', eventDate: '', guestCount: '', message: '', hp_field: ''
  });
  const [dsgvoConsent, setDsgvoConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      // Convert empty strings to null for optional fields
      const payload = {
        ...formData,
        eventDate: formData.eventDate || null,
        guestCount: formData.guestCount ? parseInt(formData.guestCount, 10) : null,
        message: formData.message || null
      };
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (response.ok) {
        if (result.emailSent) {
          trackEvent('form_submit');
          trackLeadFormConversion();
          setSubmitStatus({ type: 'success', message: result.message });
          setFormData({ name: '', phone: '', email: '', eventType: '', eventDate: '', guestCount: '', message: '', hp_field: '' });
          setDsgvoConsent(false);
        } else {
          setSubmitStatus({ type: 'warning', message: 'Ihre Anfrage wurde gespeichert, aber die E-Mail konnte nicht versendet werden. Bitte rufen Sie uns direkt an: +49 179 3912947' });
        }
      } else {
        setSubmitStatus({ type: 'error', message: result.detail || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.' });
      }
    } catch {
      setSubmitStatus({ type: 'error', message: 'Verbindungsfehler. Bitte rufen Sie uns direkt an: +49 179 3912947' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact-form-section section-padding" id="kontakt" data-testid="contact-section">
      <div className="container">
        <div className="contact-form-wrapper">
          <div className="form-header">
            <h2 className="heading-1">{contact?.sectionTitle || 'Jetzt unverbindlich anfragen'}</h2>
            <p className="body-large space-top-md">{contact?.sectionDescription || ''}</p>
          </div>
          <form className="contact-form space-top-xl" onSubmit={handleSubmit} data-testid="contact-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name" className="form-label">Name *</label>
                <input type="text" id="name" name="name" className="form-input" value={formData.name} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="phone" className="form-label">Telefon *</label>
                <input type="tel" id="phone" name="phone" className="form-input" value={formData.phone} onChange={handleChange} required />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="email" className="form-label">E-Mail *</label>
              <input type="email" id="email" name="email" className="form-input" value={formData.email} onChange={handleChange} required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="eventType" className="form-label">Art des Events *</label>
                <select id="eventType" name="eventType" className="form-input" value={formData.eventType} onChange={handleChange} required>
                  <option value="">Bitte waehlen</option>
                  <option value="Hochzeit">Hochzeit</option>
                  <option value="Geburtstag">Geburtstag</option>
                  <option value="Firmenfeier">Firmenfeier</option>
                  <option value="Kulturelle Veranstaltung">Kulturelle Veranstaltung</option>
                  <option value="Sonstiges">Sonstiges</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="eventDate" className="form-label">Gewuenschtes Datum</label>
                <input type="date" id="eventDate" name="eventDate" className="form-input" value={formData.eventDate} onChange={handleChange} />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="guestCount" className="form-label">Anzahl der Gaeste</label>
              <input type="number" id="guestCount" name="guestCount" className="form-input" placeholder="z.B. 150" value={formData.guestCount} onChange={handleChange} min="1" max="500" />
            </div>
            <div className="form-group">
              <label htmlFor="message" className="form-label">Nachricht (optional)</label>
              <textarea id="message" name="message" className="form-input form-textarea" rows="4" placeholder="Erzaehlen Sie uns von Ihren Wuenschen..." value={formData.message} onChange={handleChange}></textarea>
            </div>
            {/* Honeypot - hidden from users */}
            <input type="text" name="hp_field" value={formData.hp_field} onChange={handleChange} style={{position:'absolute',left:'-9999px',opacity:0,height:0}} tabIndex={-1} autoComplete="off" />
            {/* DSGVO Consent Checkbox */}
            <div className="form-group" style={{display:'flex', gap:'10px', alignItems:'flex-start'}}>
              <input
                type="checkbox"
                id="dsgvoConsent"
                checked={dsgvoConsent}
                onChange={(e) => setDsgvoConsent(e.target.checked)}
                required
                style={{marginTop:'4px', flexShrink:0, accentColor:'#c8a96e'}}
                data-testid="dsgvo-checkbox"
              />
              <label htmlFor="dsgvoConsent" className="form-label" style={{fontSize:'13px', lineHeight:'1.5', margin:0, cursor:'pointer'}}>
                Ich stimme der Verarbeitung meiner Daten gemaess der <a href="/datenschutz" target="_blank" rel="noopener noreferrer" style={{color:'#c8a96e'}}>Datenschutzerklaerung</a> zu und willige in die Kontaktaufnahme zur Bearbeitung meiner Anfrage ein. *
              </label>
            </div>
            {submitStatus && <div className={`submit-message ${submitStatus.type}`} data-testid="form-status">{submitStatus.message}</div>}
            <button type="submit" className="btn-primary" disabled={isSubmitting || !dsgvoConsent} data-testid="form-submit-button">
              {isSubmitting ? 'Wird gesendet...' : 'Kostenlose Anfrage senden'}
              <Send size={18} />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
